import React, { useMemo, useState } from 'react';
import rppi from '../../data/cbc-rppi.json';
import { cityToDistrict, DISTRICT_LABEL } from '../../lib/neighbourhood/cityToDistrict';
import './PropertyPriceIndexChart.css';

const RANGES = [
  { id: '1y',  label: '1Y',  mode: 'quarterly' },
  { id: '2y',  label: '2Y',  mode: 'annual', back: 2 },
  { id: '3y',  label: '3Y',  mode: 'annual', back: 3 },
  { id: '5y',  label: '5Y',  mode: 'annual', back: 5 },
  { id: 'all', label: 'All', mode: 'annual', back: null },
];
const TYPES = [
  { id: 'total',      label: 'Total' },
  { id: 'houses',     label: 'Houses' },
  { id: 'apartments', label: 'Apartments' },
];
const BASELINE = 100;

function periodLabel(p) {
  if (p.endsWith('Q1') || p.endsWith('Q2') || p.endsWith('Q3') || p.endsWith('Q4')) {
    return p.replace(/^(\d{4})Q(\d)$/, 'Q$2 $1');
  }
  return p;
}

function selectSeries({ rangeId, typeId, district }) {
  const range = RANGES.find(r => r.id === rangeId);
  if (range.mode === 'quarterly') {
    return {
      data: rppi.cyprus_quarterly[typeId],
      scope: 'Cyprus (national)',
      kind: 'quarterly',
    };
  }
  const districtKey = district || null;
  const all = districtKey
    ? rppi.districts[districtKey][typeId]
    : rppi.cyprus_annual[typeId];
  const data = range.back == null ? all : all.slice(-(range.back + 1));
  return {
    data,
    scope: districtKey ? `${DISTRICT_LABEL[districtKey]} district` : 'Cyprus (national)',
    kind: 'annual',
  };
}

export default function PropertyPriceIndexChart({ city }) {
  const [rangeId, setRangeId] = useState('5y');
  const [typeId, setTypeId]   = useState('total');
  const [hoverIdx, setHoverIdx] = useState(null);

  const district = useMemo(() => cityToDistrict(city), [city]);
  const { data, scope, kind } = useMemo(
    () => selectSeries({ rangeId, typeId, district }),
    [rangeId, typeId, district]
  );

  const w = 640, h = 240;
  const padL = 44, padR = 24, padT = 18, padB = 36;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;

  const values = data.map(d => d.value);
  const minV = Math.min(...values, BASELINE);
  const maxV = Math.max(...values, BASELINE);
  const span = (maxV - minV) || 1;
  const yMin = minV - span * 0.08;
  const yMax = maxV + span * 0.08;
  const yRange = yMax - yMin;

  const xFor = i => padL + (data.length === 1 ? innerW / 2 : (i / (data.length - 1)) * innerW);
  const yFor = v => padT + innerH - ((v - yMin) / yRange) * innerH;

  const points = data.map((d, i) => [xFor(i), yFor(d.value)]);
  const polyline = points.map(p => p.join(',')).join(' ');

  const latest = data[data.length - 1];
  const baselineY = yFor(BASELINE);
  const ticks = 4;
  const yTicks = Array.from({ length: ticks + 1 }, (_, i) => yMin + (yRange * i) / ticks);

  // X-axis label decimation — at most ~8 labels
  const labelStep = Math.max(1, Math.ceil(data.length / 8));

  const pctSinceBase = ((latest.value - BASELINE) / BASELINE * 100);
  const pctSign = pctSinceBase >= 0 ? '+' : '';

  const hover = hoverIdx != null ? data[hoverIdx] : null;

  return (
    <div className="ppi-chart">
      <header className="ppi-chart__head">
        <div>
          <h3 className="ppi-chart__title">Property Price Index</h3>
          <p className="ppi-chart__sub">
            Base: 2010 Q1 = 100 · {scope} · Source: Central Bank of Cyprus
          </p>
        </div>
        <div className="ppi-chart__latest" aria-label="Latest reading">
          <span className="ppi-chart__latest-num">{latest.value.toFixed(1)}</span>
          <span className="ppi-chart__latest-meta">
            {periodLabel(latest.period)} · {pctSign}{pctSinceBase.toFixed(1)}% since 2010
          </span>
        </div>
      </header>

      <div className="ppi-chart__controls">
        <div className="ppi-chart__seg" role="tablist" aria-label="Time range">
          {RANGES.map(r => (
            <button
              key={r.id}
              role="tab"
              aria-selected={rangeId === r.id}
              className={`ppi-chart__seg-btn ${rangeId === r.id ? 'is-active' : ''}`}
              onClick={() => { setRangeId(r.id); setHoverIdx(null); }}
            >{r.label}</button>
          ))}
        </div>
        <div className="ppi-chart__seg" role="tablist" aria-label="Property type">
          {TYPES.map(ty => (
            <button
              key={ty.id}
              role="tab"
              aria-selected={typeId === ty.id}
              className={`ppi-chart__seg-btn ${typeId === ty.id ? 'is-active' : ''}`}
              onClick={() => { setTypeId(ty.id); setHoverIdx(null); }}
            >{ty.label}</button>
          ))}
        </div>
      </div>

      <div className="ppi-chart__svg-wrap">
        <svg
          className="ppi-chart__svg"
          viewBox={`0 0 ${w} ${h}`}
          preserveAspectRatio="none"
          onMouseLeave={() => setHoverIdx(null)}
        >
          {/* Hairline grid */}
          {yTicks.map((v, i) => {
            const y = yFor(v);
            return (
              <g key={i}>
                <line
                  x1={padL} x2={w - padR}
                  y1={y} y2={y}
                  stroke="#0E0E10" strokeOpacity="0.10" strokeWidth="1"
                />
                <text
                  x={padL - 8} y={y + 4}
                  fontSize="10" textAnchor="end"
                  fill="#0E0E10" fillOpacity="0.55"
                  fontFamily="var(--font-body, sans-serif)"
                >{Math.round(v)}</text>
              </g>
            );
          })}

          {/* 2010 baseline */}
          {baselineY > padT && baselineY < padT + innerH && (
            <g>
              <line
                x1={padL} x2={w - padR}
                y1={baselineY} y2={baselineY}
                stroke="#0E0E10" strokeOpacity="0.35"
                strokeWidth="1" strokeDasharray="3 4"
              />
              <text
                x={w - padR - 4} y={baselineY - 4}
                fontSize="9" textAnchor="end"
                fill="#0E0E10" fillOpacity="0.55"
                fontFamily="var(--font-body, sans-serif)"
              >2010 baseline (100)</text>
            </g>
          )}

          {/* Gold line */}
          <polyline
            points={polyline}
            fill="none"
            stroke="#B8924A"
            strokeWidth="2"
            strokeLinecap="square"
            strokeLinejoin="miter"
          />

          {/* Dots + hover hit areas */}
          {points.map(([x, y], i) => (
            <g key={i}>
              <rect
                x={x - 22} y={padT}
                width={44} height={innerH}
                fill="transparent"
                onMouseEnter={() => setHoverIdx(i)}
                onFocus={() => setHoverIdx(i)}
                tabIndex={-1}
              />
              <rect
                x={x - 3} y={y - 3}
                width={6} height={6}
                fill={hoverIdx === i ? '#B8924A' : (i === points.length - 1 ? '#B8924A' : '#FAF9F6')}
                stroke="#B8924A" strokeWidth="1.5"
                style={{ pointerEvents: 'none' }}
              />
              {i % labelStep === 0 && (
                <text
                  x={x} y={h - 10}
                  fontSize="10" textAnchor="middle"
                  fill="#0E0E10" fillOpacity="0.55"
                  fontFamily="var(--font-body, sans-serif)"
                >{periodLabel(data[i].period)}</text>
              )}
            </g>
          ))}

          {/* Crosshair */}
          {hoverIdx != null && (
            <line
              x1={xFor(hoverIdx)} x2={xFor(hoverIdx)}
              y1={padT} y2={padT + innerH}
              stroke="#0E0E10" strokeOpacity="0.30"
              strokeWidth="1" strokeDasharray="3 3"
              style={{ pointerEvents: 'none' }}
            />
          )}
        </svg>

        {hover && (
          <div
            className="ppi-chart__tip"
            style={{
              left: `${(xFor(hoverIdx) / w) * 100}%`,
              transform: hoverIdx > data.length * 0.6
                ? 'translate(-100%, -100%) translate(-8px, -4px)'
                : 'translate(8px, -100%) translate(0, -4px)',
            }}
          >
            <span className="ppi-chart__tip-period">{periodLabel(hover.period)}</span>
            <span className="ppi-chart__tip-val">
              {hover.value.toFixed(1)}
              <span className="ppi-chart__tip-pct">
                ({hover.value >= BASELINE ? '+' : ''}
                {((hover.value - BASELINE) / BASELINE * 100).toFixed(1)}% vs 2010)
              </span>
            </span>
          </div>
        )}
      </div>

      <p className="ppi-chart__note">
        {kind === 'quarterly'
          ? '1Y view shows the last 5 quarterly readings (Cyprus-wide). Other ranges use per-district annual averages plus the latest quarterly point.'
          : 'Annual values are the average of four quarters per CBC. Q1 2026 is the most recent quarterly reading.'}
      </p>
    </div>
  );
}
